import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from '../../utils/toast';
import CustomPageForm from './CustomPageForm';

const CustomPages = () => {
  const { checkPermission } = useAuth();
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const canCreate = checkPermission('Create Custom Page');
  const canUpdate = checkPermission('Update Custom Page');
  const canDelete = checkPermission('Delete Custom Page');

  useEffect(() => {
    fetchCustomPages();
  }, [pagination.page, searchTerm, selectedCategory]);

  const fetchCustomPages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await api.get(`/custom-pages?${params}`);
      
      if (response.data.success) {
        setCustomPages(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));

        // Extract unique categories
        const uniqueCategories = [...new Set(response.data.data.map(page => page.menuCategory))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error fetching custom pages:', error);
      toast.error('Failed to fetch custom pages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPage(null);
    setShowForm(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    console.log("Editing Page", page);
    setShowForm(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this custom page?')) {
      return;
    }

    try {
      const response = await api.delete(`/custom-pages/${pageId}`);
      
      if (response.data.success) {
        toast.success('Custom page deleted successfully');
        fetchCustomPages();
        // Refresh navigation to update sidebar
        if (window.refreshSidebarNavigation) {
          window.refreshSidebarNavigation();
        }
      }
    } catch (error) {
      console.error('Error deleting custom page:', error);
      toast.error('Failed to delete custom page');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingPage) {
        const response = await api.put(`/custom-pages/${editingPage._id}`, formData);
        if (response.data.success) {
          toast.success('Custom page updated successfully');
        }
      } else {
        const response = await api.post('/custom-pages', formData);
        if (response.data.success) {
          toast.success('Custom page created successfully');
        }
      }
      
      setShowForm(false);
      setEditingPage(null);
      fetchCustomPages();
      // Refresh navigation to update sidebar
      if (window.refreshSidebarNavigation) {
        window.refreshSidebarNavigation();
      }
    } catch (error) {
      console.error('Error saving custom page:', error);
      toast.error(error.response?.data?.message || 'Failed to save custom page');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'save': return 'Save Only';
      case 'create_task': return 'Create Task';
      case 'create_invoice': return 'Create Invoice';
      default: return action;
    }
  };

  if (loading && customPages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Pages</h1>
          <p className="text-gray-600">Manage custom data entry and view pages</p>
        </div>
        {canCreate && (
          <button
            onClick={handleCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create New Page
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {/* Pages List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Menu Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fields
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customPages.map((page) => (
                <tr key={page._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{page.name}</div>
                      {page.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{page.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {page.menuName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {page.menuCategory}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {page.fields?.length || 0} fields
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getActionLabel(page.formSubmissionAction)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      page.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {page.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {canUpdate && (
                      <button
                        onClick={() => handleEdit(page)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(page._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {customPages.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No custom pages found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <CustomPageForm
          page={editingPage}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingPage(null);
          }}
        />
      )}
    </div>
  );
};

export default CustomPages; 